/**
 * BlockCanvas — the one drag-and-drop surface every journey type's page uses.
 *
 * WHY THIS EXISTS
 * There were two block canvases: the designer's own (`blockView` over
 * `page.content`) and One-Stop's (`OSCanvas` over `page.osBlocks`). They looked
 * alike and were separately written, so on 2026-09-02 the SAME defect had to be
 * fixed twice in three different ways — the 8px drop target, the preview
 * rendering the wrong array, and the per-block controls. Each time one side was
 * fixed and the other was still wrong, and the operator's word for the result was
 * that One-Stop "feels different because it is harder to use".
 *
 * WHAT IS SHARED AND WHAT IS NOT
 * This owns everything that is the same regardless of what a block IS: the drop
 * geometry, the insertion indicator, selection, hover, drag-to-reorder, the
 * per-block controls, and the space to aim below the last block.
 *
 * It does NOT own how a block LOOKS. That is `renderBlock`, and it has to stay a
 * prop: the designer draws its own approximation of a page, while One-Stop mounts
 * the student's shipping renderer. Collapsing those would mean re-drawing queue
 * cards in the designer, which is the fork both canvases exist to avoid.
 *
 * So: one gesture, two vocabularies. A fix to how dragging feels lands in one
 * place; a change to what a block looks like stays with the thing that knows.
 */
import { Fragment, useRef, useState } from "react";
import type { CSSProperties, DragEvent as RDragEvent, JSX, ReactNode } from "react";
import { gapIndexFromPointer } from "../shared/editor-shell";

/** The two drag payloads, shared so a palette and a canvas cannot disagree. */
export const BLOCK_ADD_DND = "application/x-nj-block";
export const BLOCK_MOVE_DND = "application/x-nj-move";

export interface CanvasBlockRef {
  /** Stable identity for React and for the move/remove callbacks. */
  uid: string;
  /** What the author sees named in controls and refusals. */
  id: string;
}

export interface BlockCanvasProps<B extends CanvasBlockRef> {
  blocks: readonly B[];
  /** Selected block id (`CanvasBlockRef.id`), or null. */
  sel: string | null;
  onSelect: (id: string | null) => void;
  onMove: (from: number, to: number) => void;
  onInsert: (type: string, at: number) => void;
  onRemove: (uid: string) => void;
  /** How this journey type draws one block. */
  renderBlock: (block: B, index: number) => ReactNode;
  /** Human name for the controls' tooltips. */
  labelOf?: (block: B) => string;
  /** Preview: no drag, no controls, no selection ring. */
  readOnly?: boolean;
  /** Horizontal inset for the indicator, matching the blocks' own margin. */
  inset?: number;
  emptyState?: ReactNode;
}

const CTL: CSSProperties = {
  width: 20, height: 20, border: "1px solid #DCCCFC", borderRadius: 4, background: "#FFFFFF",
  color: "#5142C7", fontSize: 10, lineHeight: 1, cursor: "pointer", fontFamily: "inherit",
};

export function BlockCanvas<B extends CanvasBlockRef>(props: BlockCanvasProps<B>): JSX.Element {
  const [dragUid, setDragUid] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const [hoverUid, setHoverUid] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inset = props.inset ?? 14;

  const kinds = (e: RDragEvent<HTMLElement>) => ({
    add: !!e.dataTransfer?.types?.includes(BLOCK_ADD_DND),
    move: !!e.dataTransfer?.types?.includes(BLOCK_MOVE_DND),
  });
  const gapAt = (clientY: number) => gapIndexFromPointer(listRef.current, clientY, "[data-canvas-block]");

  /* The LIST is the drop target, not the gaps. A gap-sized target is an 8px
     strip you have to hit before anything tells you where it is; this way every
     pixel answers, and the answer flips at each block's midpoint — which is what
     every list-reorder UI does and therefore needs no learning. */
  const onDragOver = (e: RDragEvent<HTMLElement>) => {
    if (props.readOnly) return;
    const k = kinds(e);
    if (!k.add && !k.move) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = k.move ? "move" : "copy";
    const at = gapAt(e.clientY);
    if (dropAt !== at) setDropAt(at);
  };

  const onDrop = (e: RDragEvent<HTMLElement>) => {
    if (props.readOnly) return;
    const type = e.dataTransfer?.getData(BLOCK_ADD_DND);
    const uid = e.dataTransfer?.getData(BLOCK_MOVE_DND);
    e.preventDefault();
    const at = dropAt ?? gapAt(e.clientY);
    setDragUid(null); setDropAt(null);
    if (uid) {
      const from = props.blocks.findIndex((b) => b.uid === uid);
      /* Either gap touching the block is where it already is. Returning without
         writing keeps a fumbled drag out of the undo stack, so ctrl-Z reaches the
         edit before it rather than the fumble. */
      if (from >= 0 && at !== from && at !== from + 1) props.onMove(from, at > from ? at - 1 : at);
      return;
    }
    if (type) props.onInsert(type, at);
  };

  const nudge = (uid: string, delta: number) => {
    const from = props.blocks.findIndex((b) => b.uid === uid);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= props.blocks.length) return;
    props.onMove(from, to);
  };

  /** Drawn, never hit — nothing lands on the indicator. */
  const indicator = (at: number) =>
    dropAt === at && !props.readOnly
      ? <div style={{ height: 3, borderRadius: 999, background: "#5142C7", margin: `1px ${inset}px`, pointerEvents: "none" }} />
      : null;

  if (props.blocks.length === 0) {
    return (
      <div
        ref={listRef}
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{ padding: `0 ${inset}px 12px` }}
      >
        {props.emptyState ?? null}
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      onDragOver={onDragOver}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropAt(null); }}
      onDrop={onDrop}
      /* Room to aim BELOW the last block. Without it, appending meant hovering
         the lower half of the final block — the same precision problem one
         element up. */
      style={{ paddingBottom: 28 }}
    >
      {props.blocks.map((block, i) => {
        const on = props.sel === block.id && !props.readOnly;
        const showCtl = !props.readOnly && (props.sel === block.id || hoverUid === block.uid);
        const label = props.labelOf?.(block) ?? block.id;
        return (
          <Fragment key={block.uid}>
            {indicator(i)}
            <div
              data-canvas-block=""
              draggable={!props.readOnly}
              onDragStart={(e) => {
                if (props.readOnly) { e.preventDefault(); return; }
                e.stopPropagation();
                e.dataTransfer?.setData(BLOCK_MOVE_DND, block.uid);
                if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
                setDragUid(block.uid);
              }}
              onDragEnd={() => { setDragUid(null); setDropAt(null); }}
              onMouseEnter={() => { if (!props.readOnly && hoverUid !== block.uid) setHoverUid(block.uid); }}
              onMouseLeave={() => { if (hoverUid === block.uid) setHoverUid(null); }}
              onClick={(e) => { if (props.readOnly) return; e.stopPropagation(); props.onSelect(on ? null : block.id); }}
              style={{
                position: "relative", borderRadius: 8,
                /* Vertical margin so blocks never touch (operator, 2026-09-02).
                   Touching blocks read as one block, and the selection outline of
                   two adjacent ones merged into a single box. It lives HERE rather
                   than on each block's own render so every journey type gets the
                   same rhythm and no renderer has to remember it. */
                margin: `10px ${inset}px`,
                outline: on ? "2px solid #5142C7" : "none", outlineOffset: 2,
                cursor: props.readOnly ? "default" : "grab",
                opacity: dragUid === block.uid ? 0.4 : 1,
              }}
            >
              {/* Remove on hover, reorder on selection. The × appears as soon as
                  the pointer is over the block so it can go without being
                  selected first; the arrows only when selected, which keeps them
                  off screen while still leaving a path that is not a drag. */}
              {showCtl ? (
                <span
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: "absolute", top: -11, right: 4, zIndex: 3, display: "inline-flex", gap: 3, background: "#FFFFFF", padding: 2, borderRadius: 6, boxShadow: "0 1px 4px rgba(20,19,31,0.16)" }}
                >
                  {props.sel === block.id ? (<>
                    <button onClick={() => nudge(block.uid, -1)} disabled={i === 0} title="Move up" style={{ ...CTL, opacity: i === 0 ? 0.35 : 1 }}>↑</button>
                    <button onClick={() => nudge(block.uid, 1)} disabled={i === props.blocks.length - 1} title="Move down" style={{ ...CTL, opacity: i === props.blocks.length - 1 ? 0.35 : 1 }}>↓</button>
                  </>) : null}
                  <button onClick={() => props.onRemove(block.uid)} title={`Remove ${label}`} style={{ ...CTL, borderColor: "#F3D0CB", color: "#C0392B", fontSize: 11 }}>×</button>
                </span>
              ) : null}
              {props.renderBlock(block, i)}
            </div>
          </Fragment>
        );
      })}
      {indicator(props.blocks.length)}
    </div>
  );
}
